/**
 * ⚖ S7 — the `categorias` lens for the Acervo.
 *
 * The SECOND axis over the same `sources/*` set. The `tipos` lens (modality,
 * `case-files-view.ts`) answers *"what KIND of file is this?"*; this one
 * answers *"what ROLE does it play in the case?"*. A source is **categorized,
 * never moved** — both lenses are views over one folder.
 *
 * ---
 *
 * ## Three rules this file exists to keep
 *
 * **1. Group on the EFFECTIVE category — `category ?? suggested_category`.**
 * Category is written DETERMINISTICALLY AT INGEST from a doctype→category
 * table (no LLM, no turn), so `suggested_category` is present from birth and
 * most sources are *provisionally* classified. Keying off "was it assessed"
 * would file every fresh source under "awaiting classification" while the
 * server had already classified it — a false claim built from a negative, and
 * the exact defect that killed the earlier "Pendente" group.
 *
 * **2. NEVER hardcode a category id.** They are server-owned DATA. They have
 * already changed language once (`peticoes` → `petition`), and a hardcoded
 * list of either language finds nothing and renders an EMPTY axis — which
 * reads to a lawyer as *"nothing is classified"*. Every id here comes from the
 * payload; the registry supplies labels and order, and an id with no registry
 * row still renders (rule 3).
 *
 * **3. An UNKNOWN id must still appear.** A category the registry has not
 * caught up with is real data about a real file. Dropping it would hide the
 * lawyer's document; it renders with its raw id as the label, which is ugly
 * and visible — the honest failure mode, not the silent one.
 *
 * ⚠ **No default lens is decided here.** Which axis the Acervo opens on is
 * contested and sits with the operator; see `ACERVO_DEFAULT_LENS` in
 * `case-files-lens.ts` — one constant, flippable without touching this logic.
 */

import type { CaseCategoryEntry, CaseSourceCatalogRow } from "@/types/gateway";

/** Sources whose effective category is absent entirely. */
export const UNCATEGORIZED_GROUP = "__uncategorized__";

export interface CategoryGroup {
  /** The served `categoryId`, or `UNCATEGORIZED_GROUP`. */
  key: string;
  /** Rendered label. Registry label for the active locale → any locale it has
   *  → the raw id. Never empty, never fabricated. */
  label: string;
  emoji?: string;
  /** True when no registry row matched — the label is the raw id and the UI
   *  may want to mark it. Distinguishes "new category" from "known one". */
  unregistered: boolean;
  rows: CaseSourceCatalogRow[];
}

/**
 * The effective category of one source.
 *
 * `category` wins when present; otherwise the doctype prior. Returns
 * `undefined` only when the server offered neither — which is NOT the same as
 * "uncategorized by decision", and the caller must keep that distinction
 * (see `UNCATEGORIZED_GROUP`).
 *
 * ⚠ **BOTH FIELDS ARE NESTED UNDER `assessment`.** The index-builder is the
 * sole `_index.json` writer and emits nested; the runtime's own gateway
 * docblock says so, and the LexIDE badge already reads `row.assessment`. The
 * first cut of this function read them at TOP LEVEL, so every row resolved to
 * `undefined` and the entire Acervo landed in "sem categoria" — a lens that
 * looked built and classified nothing. Diagnosed by `mission-ingest` off a live
 * probe case (2026-08-07).
 *
 * The top-level read is kept as a tolerant FALLBACK, not as the primary: it
 * costs nothing, and a flattened row from some future writer still groups
 * instead of silently vanishing into the uncategorized bucket.
 *
 * ⚠ **THE WIRE SPELLS "NO VERDICT YET" AS `""`, NOT AS ABSENT** (measured live
 * 2026-08-10 on the 176-source Nadir corpus). `category` is the `assess-content`
 * VERDICT and `suggested_category` is the ingest doctype prior, so a source that
 * has been ingested but not yet assessed arrives as
 * `{category: "", suggested_category: "correspondence"}`. `??` falls through on
 * null/undefined ONLY — an empty-string `category` therefore SHADOWED a
 * perfectly good suggestion, and 126 of those 176 sources (72%) rendered under
 * "sem categoria" while the server had classified every one of them. Same
 * false-claim-from-a-negative this file's rule 1 exists to prevent, arriving
 * through the empty-string door instead of the nesting door.
 *
 * So: take the first NON-BLANK of the four, never merely the first defined.
 */
export function effectiveCategory(row: CaseSourceCatalogRow): string | undefined {
  const a = row.assessment;
  return [a?.category, a?.suggested_category, row.category, row.suggested_category].find(
    (c): c is string => typeof c === "string" && c.trim().length > 0,
  );
}

/**
 * Is the category axis servable at all?
 *
 * ⚠ LEVEL, not truthiness: `>= 1` serves grouping; `>= 2` additionally means
 * the evaluation block and the assess-content harness exist. `if (meta)` would
 * pass at `1` and render an evaluation surface the runtime cannot fill.
 */
export function categoryAxisAvailable(categoryMeta: number | undefined): boolean {
  return typeof categoryMeta === "number" && categoryMeta >= 1;
}

/** Does the runtime back the EVALUATION surface (not merely grouping)? */
export function categoryEvaluationAvailable(categoryMeta: number | undefined): boolean {
  return typeof categoryMeta === "number" && categoryMeta >= 2;
}

function labelFor(
  id: string,
  registry: ReadonlyMap<string, CaseCategoryEntry>,
  locale: string,
): { label: string; emoji?: string; unregistered: boolean } {
  const entry = registry.get(id);
  if (!entry) return { label: id, unregistered: true };
  const label =
    entry.labels?.[locale] ?? Object.values(entry.labels ?? {})[0] ?? id;
  return { label, emoji: entry.emoji, unregistered: false };
}

/**
 * Group catalog rows by their effective category.
 *
 * Order: registry `order` ascending, then label. Unregistered ids sort after
 * registered ones (they have no declared position — inventing one would imply
 * a placement the server never stated). `UNCATEGORIZED_GROUP` sorts last.
 *
 * Empty groups are NOT emitted: a category with no documents is a destination
 * in the classify surface, never a browse row.
 */
export function buildCategoryGroups(
  rows: readonly CaseSourceCatalogRow[],
  categories: readonly CaseCategoryEntry[] | undefined,
  locale: string,
): CategoryGroup[] {
  const registry = new Map<string, CaseCategoryEntry>();
  for (const c of categories ?? []) {
    if (c && typeof c.categoryId === "string") registry.set(c.categoryId, c);
  }

  const byKey = new Map<string, CaseSourceCatalogRow[]>();
  for (const row of rows) {
    const key = effectiveCategory(row) ?? UNCATEGORIZED_GROUP;
    const list = byKey.get(key);
    if (list) list.push(row);
    else byKey.set(key, [row]);
  }

  const groups: CategoryGroup[] = [];
  for (const [key, groupRows] of byKey) {
    if (key === UNCATEGORIZED_GROUP) {
      groups.push({
        key,
        // Labelled by the caller via i18n — this module does not own copy.
        label: UNCATEGORIZED_GROUP,
        unregistered: false,
        rows: groupRows,
      });
      continue;
    }
    const { label, emoji, unregistered } = labelFor(key, registry, locale);
    groups.push({ key, label, emoji, unregistered, rows: groupRows });
  }

  const orderOf = (g: CategoryGroup): number => {
    if (g.key === UNCATEGORIZED_GROUP) return Number.MAX_SAFE_INTEGER;
    const o = registry.get(g.key)?.order;
    // Unregistered → after every registered row, before "uncategorized".
    return typeof o === "number" ? o : Number.MAX_SAFE_INTEGER - 1;
  };

  groups.sort((a, b) => {
    const d = orderOf(a) - orderOf(b);
    return d !== 0 ? d : a.label.localeCompare(b.label);
  });
  return groups;
}

/**
 * The lens the Acervo actually renders: the SAME `WorkspaceTreeFile[]` the
 * modality lens shows, grouped by category instead.
 *
 * ⚠ Deliberately groups FILES, not catalog rows. The Acervo already knows how
 * to render a file (sort, drag, rename, the info dialog); regrouping catalog
 * rows would have meant a second render path for the same objects, and the two
 * would drift. The catalog is consulted ONLY to answer "what category is this
 * file?" — it is a lookup, not a source of rows.
 *
 * The join is `processed_ref` → the tree file's `path`. Both are
 * case-root-relative and written by the same pipeline.
 *
 * ⚠ A file with no catalog row is NOT dropped — it lands in
 * `UNCATEGORIZED_GROUP` like any uncategorized source. The catalog can lag the
 * tree (a file written but not yet indexed), and a lawyer's document must
 * never vanish because an index has not caught up.
 */
export function buildCategoryFileGroups<T extends { path: string }>(
  files: readonly T[],
  rows: readonly CaseSourceCatalogRow[] | undefined,
  categories: readonly CaseCategoryEntry[] | undefined,
  locale: string,
): { key: string; label: string; emoji?: string; unregistered: boolean; files: T[] }[] {
  const categoryByPath = new Map<string, string>();
  for (const r of rows ?? []) {
    const ref = r.processed_ref;
    const cat = effectiveCategory(r);
    if (ref && cat) categoryByPath.set(ref, cat);
  }

  // Reuse the row grouper for ordering + labelling by synthesising one row per
  // distinct key, so ordering rules live in exactly one place.
  const byKey = new Map<string, T[]>();
  for (const f of files) {
    const key = categoryByPath.get(f.path) ?? UNCATEGORIZED_GROUP;
    const list = byKey.get(key);
    if (list) list.push(f);
    else byKey.set(key, [f]);
  }

  const skeleton = buildCategoryGroups(
    [...byKey.keys()].map((k) => ({
      source_id: k,
      processed_name: k,
      // ⚠ NESTED, matching the wire — these synthetic rows are read back by
      // `effectiveCategory` through the same path as real ones.
      // A SYNTHETIC row, not a wire row: it exists only to carry the key back
      // through the shared ordering/labelling path. Cast rather than
      // fabricating a plausible `status`, which would be inventing census data.
      assessment: (k === UNCATEGORIZED_GROUP
        ? null
        : { category: k }) as CaseSourceCatalogRow["assessment"],
    })),
    categories,
    locale,
  );

  return skeleton.map((g) => ({
    key: g.key,
    label: g.label,
    emoji: g.emoji,
    unregistered: g.unregistered,
    files: byKey.get(g.key) ?? [],
  }));
}
